import React, { FC, useEffect } from 'react';
import { UitkLayoutGrid, UitkLayoutGridItem, UitkLayoutGridItemProps } from 'uitk-react-layout-grid';
import { UitkSpacing, UitkSpacingProps } from 'uitk-react-spacing';
import { ErrorBoundary } from 'bernie-view';
import { useBexApi } from '@shared-ui/bex-api-context';
import { GenericErrorMessage } from '../common/generic-error-message';
import useNavigationParams from '../../hooks/useNavigationParams';
import EgMarkdownRenderer from '@eg-markdown-renderer/core';
import DocumentErrorView from './document-error-view';
import LoadingSkeleton from '../documentation-page/loading-skeleton';
import { useDocumentQuery } from '../../graphql/__generated__/types';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import Footer from '../footer';
import MessageBanner from '../common/message-banner';
import useAnalytics from '../../hooks/analytics/useAnalytics';
import { useMarkdownRendererOptions } from '../../hooks/useMarkdownRendererOptions';
import useMessageBanner from 'hooks/useMessageBanner';
import HeaderTitle from '../header-title';

const HomeDocumentView: FC = () => {
  const { hubId, hash, documentId } = useNavigationParams();
  const { context } = useBexApi();
  const { setMessageBanner } = useMessageBanner();
  const { data, loading, error } = useDocumentQuery({ variables: { context, hubId, documentId } });
  const analytics = useAnalytics();
  const { componentsOptions } = useMarkdownRendererOptions(hubId);

  useEffect(() => {
    if (!hash) document?.getElementById('document')?.scrollTo(0, 0);
  }, [documentId]);

  const documentTypeName = data?.documentation?.document?.__typename;

  useEffect(() => {
    if (!loading && !error && documentTypeName === 'DocumentationDocumentSuccessResponse') {
      analytics.trackPageView();
    }
  }, [documentId, loading, error]);

  React.useEffect(() => {
    if (
      !loading &&
      documentTypeName !== 'DocumentationDocumentRedirect' &&
      data?.documentation?.document?.notifications !== null
    ) {
      setMessageBanner({
        message: data.documentation?.document?.notifications[0]?.message,
        iconName: data.documentation?.document?.notifications[0]?.icon.token,
        messageStyle: { marginLeft: '1rem' },
      });
    }
  }, [data?.documentation?.document]);

  if (error || documentTypeName === 'DocumentationDocumentRedirect') {
    return <GenericErrorMessage />;
  }

  if (loading || !data) {
    return (
      <UitkLayoutGrid columns={10}>
        <UitkLayoutGridItem {...documentGridColumnProps}>
          <UitkSpacing {...documentContainerSpacingProps}>
            <div>
              <LoadingSkeleton />
            </div>
          </UitkSpacing>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    );
  }

  if (documentTypeName === 'DocumentationDocumentErrorResponse') {
    return <DocumentErrorView errorResponse={data.documentation.document} />;
  }

  const { content, banner, title, intro } = data.documentation.document;

  return (
    <UitkLayoutGrid columns={10} style={{ flexGrow: 1 }}>
      <UitkLayoutGridItem {...documentGridColumnProps}>
        <UitkSpacing {...documentContainerSpacingProps}>
          <div>
            <MessageBanner
              message={banner?.message}
              iconName={banner?.icon.token}
              spacing={{ margin: { blockend: 'four' } }}
            />
            <ErrorBoundary label="document.body" key={documentId} fallback={<GenericErrorMessage />}>
              <div className="md-renderer-wrapper">
                <EgMarkdownRenderer mdBody={content} componentsOptions={componentsOptions} />
              </div>
            </ErrorBoundary>
          </div>
        </UitkSpacing>
      </UitkLayoutGridItem>
      <UitkLayoutGridItem {...footerGridColumnProps} style={{ alignSelf: 'end' }}>
        <UitkLayoutFlex direction="column">
          <UitkLayoutFlexItem>
            <div>
              <Footer />
            </div>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkLayoutGridItem>
      <HeaderTitle documentTitle={title} content={intro} />
    </UitkLayoutGrid>
  );
};

const documentGridColumnProps: UitkLayoutGridItemProps = {
  colSpan: { small: 'all' },
  colStart: { extra_large: 2, small: 1 },
  colEnd: { extra_large: 8, small: 12 },
  className: 'large-max-width',
};

const footerGridColumnProps: UitkLayoutGridItemProps = {
  ...documentGridColumnProps,
  colSpan: 'all',
  colStart: 2,
  rowStart: 0,
  rowEnd: 0,
};
const documentContainerSpacingProps: UitkSpacingProps = {
  margin: { large: { block: 'six' } },
  padding: {
    small: { inline: 'four', block: 'three' },
    large: { block: 'three' },
    extra_large: { inline: 'unset' },
  },
};
export default HomeDocumentView;
